/**
 * The app's visual language.
 *
 * This started as Tracker's palette and is being promoted to the whole app:
 * one place for colour, radius, type and spacing so pages can't drift apart
 * the way ~3,000 hardcoded hex values did.
 *
 * `constants/trackerUi.js` re-exports from here, so Tracker keeps working
 * unchanged while everything else adopts the same tokens.
 */

export const COLORS = {
  ink: "#10182b",
  inkSoft: "#4b5468",
  inkFaint: "#8890a3",
  line: "#dde2ee",
  lineSoft: "#e8ebf3",
  surface: "#ffffff",
  sunken: "#eef1f8",
  accent: "#1272B3",
  accentSoft: "#EAF6FD",
  accentInk: "#2C63AE",
  done: "#0f9d6a",
  doneSoft: "#e5f7ee",
  warn: "#b8860b",
  warnSoft: "#fbf1dc",
  danger: "#c0392b",
  dangerSoft: "#fbe9e7",
  upcoming: "#b7bdcd",
};

/** Numbers, counts, IDs and micro-captions. Tabular figures line up in columns. */
export const MONO = "'SF Mono','Roboto Mono',ui-monospace,monospace";

export const RADIUS = {
  /** Buttons, inputs, small controls. */
  control: 8,
  /** Cards and panels. */
  card: 12,
  /** Pills and count chips. */
  pill: 100,
};

/**
 * Maps the two grey families already in the codebase onto these tokens.
 * Tailwind *slate* is used by most pages; Tailwind *gray* by Issues and parts
 * of Projects. Kept here so a conversion is mechanical rather than a judgement
 * call each time.
 */
export const LEGACY_COLOR_MAP = {
  // slate
  "#1e293b": COLORS.ink,
  "#334155": COLORS.ink,
  "#475569": COLORS.inkSoft,
  "#64748b": COLORS.inkSoft,
  "#94a3b8": COLORS.inkFaint,
  "#cbd5e1": COLORS.upcoming,
  "#e2e8f0": COLORS.line,
  "#f1f5f9": COLORS.sunken,
  "#f8fafc": COLORS.sunken,
  // gray
  "#374151": COLORS.ink,
  "#6b7280": COLORS.inkSoft,
  "#9ca3af": COLORS.inkFaint,
  "#e5e7eb": COLORS.line,
  "#f9fafb": COLORS.sunken,
  // brand blues → accent
  "#1e40af": COLORS.accentInk,
  "#2563eb": COLORS.accent,
  "#3b82f6": COLORS.accent,
  "#1d4ed8": COLORS.accentInk,
  // semantic
  "#16a34a": COLORS.done,
  "#22c55e": COLORS.done,
  "#10b981": COLORS.done,
  "#dc2626": COLORS.danger,
  "#ef4444": COLORS.danger,
  "#f59e0b": COLORS.warn,
  "#d97706": COLORS.warn,
};

/** A soft-background / strong-text pair for every semantic tone. */
export const TONES = {
  neutral: { bg: COLORS.sunken, color: COLORS.inkSoft },
  accent: { bg: COLORS.accentSoft, color: COLORS.accentInk },
  done: { bg: COLORS.doneSoft, color: COLORS.done },
  warn: { bg: COLORS.warnSoft, color: COLORS.warn },
  danger: { bg: COLORS.dangerSoft, color: COLORS.danger },
};

export const PRIORITY_STYLES = {
  Critical: TONES.danger,
  High: TONES.warn,
  Medium: { bg: COLORS.sunken, color: COLORS.inkSoft },
  Low: { bg: COLORS.sunken, color: COLORS.inkFaint },
  Blocker: TONES.danger,
};

/** Uppercase micro-label that sits above a section. */
export const CAPTION_STYLE = {
  fontFamily: MONO,
  fontSize: "0.6rem",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: COLORS.inkFaint,
};

export function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export function formatShortDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}
